import React from 'react';
import ReactGA from 'react-ga4';

// Constants
export const WHATSAPP_NUMBER = "6285776203499";
export const WHATSAPP_BAGAS = WHATSAPP_NUMBER;
export const WHATSAPP_YUDHA = WHATSAPP_NUMBER;

/** URL WhatsApp dengan pesan kustom — nomor tunggal untuk semua layanan */
export const waLink = (message = "Halo, saya tertarik dengan layanan Cakra Digital Innovation. Bisa dibantu konsultasi?") =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

export const WHATSAPP_URL = waLink();
export const WHATSAPP_PRICING_URL = waLink(
  "Halo, saya tertarik dengan layanan pricing Cakra Digital Innovation. Saya ingin konsultasi tentang paket yang tersedia."
);
export const WHATSAPP_DUAL_URL = WHATSAPP_URL;
export const GA_MEASUREMENT_ID = "G-4R76MXBEC7";

// Initialize GA4
export const initGA = () => {
  ReactGA.initialize(GA_MEASUREMENT_ID, {
    gaOptions: {
      debug_mode: process.env.NODE_ENV === 'development'
    }
  });
};

// Page View Tracking
export const trackPageView = (page_path) => {
  ReactGA.send({
    hitType: 'pageview',
    page: page_path
  });
};

// CTA Click Tracking
export const trackCTAClick = (button_name, page_location) => {
  ReactGA.event({
    action: 'click_cta',
    category: 'CTA',
    label: button_name,
    customParameters: {
      button_name,
      page_location
    }
  });
};

// WhatsApp Click Tracking
export const trackWhatsAppClick = (page_location) => {
  ReactGA.event({
    action: 'click_whatsapp',
    category: 'WhatsApp',
    label: WHATSAPP_NUMBER,
    customParameters: {
      phone_number: WHATSAPP_NUMBER,
      page_location
    }
  });
};

// Scroll Depth Tracking
export const trackScrollDepth = (percent_scrolled) => {
  ReactGA.event({
    action: 'scroll_depth',
    category: 'Engagement',
    label: `${percent_scrolled}%`,
    customParameters: {
      percent_scrolled
    }
  });
};

// Time on Page Tracking
export const trackEngagementTime = (time_seconds) => {
  ReactGA.event({
    action: 'engagement_time',
    category: 'Engagement',
    label: `${time_seconds}s`,
    customParameters: {
      time_seconds
    }
  });
};

// Outbound Link Tracking
export const trackOutboundLink = (url) => {
  ReactGA.event({
    action: 'click_outbound',
    category: 'Outbound',
    label: url,
    customParameters: {
      url
    }
  });
};

// Outbound Link Component
export const OutboundLink = ({ href, children, className, ...props }) => {
  const handleClick = (e) => {
    trackOutboundLink(href);
    // Optional: Open in new tab
    if (props.target === '_blank') {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <a 
      href={href} 
      onClick={handleClick} 
      className={className}
      {...props}
    >
      {children}
    </a>
  );
};

// Form Submission Tracking
export const trackFormSubmission = (form_name) => {
  ReactGA.event({
    action: 'submit_form',
    category: 'Form',
    label: form_name,
    customParameters: {
      form_name
    }
  });
};

// Form Component with Tracking
export const TrackedForm = ({ formName, onSubmit, children, ...props }) => {
  const handleSubmit = (e) => {
    trackFormSubmission(formName);
    if (onSubmit) {
      onSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} {...props}>
      {children}
    </form>
  );
};

// Custom Event Tracking (for flexibility)
export const trackCustomEvent = (action, category, label, customParameters = {}) => {
  ReactGA.event({
    action,
    category,
    label,
    customParameters
  });
};

// Scroll Tracking Hook
export const useScrollTracking = () => {
  React.useEffect(() => {
    let scrollTracked = { 50: false, 90: false };
    
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPosition = window.scrollY;
      const scrollPercentage = Math.round((scrollPosition / scrollHeight) * 100);
      
      if (scrollPercentage >= 50 && !scrollTracked[50]) {
        trackScrollDepth(50);
        scrollTracked[50] = true;
      }
      
      if (scrollPercentage >= 90 && !scrollTracked[90]) {
        trackScrollDepth(90);
        scrollTracked[90] = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
};

// Time on Page Hook
export const useTimeOnPageTracking = () => {
  React.useEffect(() => {
    let timeTracked = { 30: false, 60: false };
    
    const timers = [
      setTimeout(() => {
        if (!timeTracked[30]) {
          trackEngagementTime(30);
          timeTracked[30] = true;
        }
      }, 30000),
      setTimeout(() => {
        if (!timeTracked[60]) {
          trackEngagementTime(60);
          timeTracked[60] = true;
        }
      }, 60000)
    ];
    
    return () => timers.forEach(timer => clearTimeout(timer));
  }, []);
};
